import {
  DataTableFilter,
  ExpensesSignedInvoiceFilter,
  FilterType,
  ProjectionKind,
  usesignedInvoicesDataTable,
} from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { Actions } from './_Actions'
import { Amount } from './_Amount'
import { DateLabel } from './_DateLabel'
import { EmptyBlock } from './_EmptyBlock'
import { Label } from './_Label'
import { Period } from './_Period'
import { Status } from './_Status'

interface SignedInvoicesDatatableProps {
  invoicesState?: string
}

const SignedInvoicesDatatable = ({ invoicesState }: SignedInvoicesDatatableProps) => {
  const { company } = useCurrentSession()
  const mainLegalEntity = company.legalEntities.find((e) => e.main)

  const source = usesignedInvoicesDataTable(
    [
      {
        identifier: 'emmiter_name',
        title: i18n.t('expenses.settings.signed_invoices.list.table.attributes.vendor'),
        kind: ProjectionKind.Custom,
        sortBy: 'emitterName',
        template: '{{ emiterName }}',
        render: ({ emitterName }) => {
          return <Label label={emitterName} />
        },
      },
      {
        identifier: 'accounting_date',
        title: i18n.t('expenses.settings.signed_invoices.list.table.attributes.accounting_date'),
        kind: ProjectionKind.Custom,
        sortBy: 'accountingDate',
        template: '{{ accountingDate }}',
        render: ({ accountingDate }) => {
          return <DateLabel date={accountingDate} />
        },
      },
      {
        identifier: 'closing_period',
        title: i18n.t('expenses.settings.signed_invoices.list.table.attributes.period'),
        kind: ProjectionKind.Custom,
        template: '{{ expensesClosingPeriod }}',
        render: ({ aeatClosingPeriodFrequency, accountingDate }) => {
          return (
            <Period
              aeatClosingPeriodFrequency={aeatClosingPeriodFrequency}
              accountingDate={accountingDate}
            />
          )
        },
      },
      {
        identifier: 'expense_status',
        title: i18n.t('expenses.settings.signed_invoices.list.table.attributes.expenseStatus'),
        kind: ProjectionKind.Custom,
        template: '{{ parentExpenseStatus }}',
        render: ({ parentExpense }) => {
          if (!parentExpense) return null
          return <Status status={parentExpense.status} />
        },
      },
      {
        identifier: 'amount',
        title: i18n.t('expenses.settings.signed_invoices.list.table.attributes.amount'),
        kind: ProjectionKind.Custom,
        sortBy: 'amount',
        template: '{{ amountCents }}',
        render: ({ amount, currency }) => {
          return <Amount amount={amount} currency={currency} />
        },
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 70,
        render: (signedInvoice) => {
          return <Actions signedInvoice={signedInvoice} />
        },
      },
    ],
    {
      variables: {
        signedForOrganization: 'aeat',
        receiverLegalEntityId: mainLegalEntity?.id,
        omitSignedDocuments: true,
        invoiceState: invoicesState,
      },
    }
  )

  const filters: DataTableFilter<ExpensesSignedInvoiceFilter>[] = [
    {
      identifier: 'expense_status',
      title: i18n.t('expenses.settings.signed_invoices.list.table.attributes.expenseStatus'),
      path: 'parentExpenseStatus',
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: [
            {
              label: i18n.t('expenses_v2.list.filters.draft'),
              value: 'draft',
            },
            {
              label: i18n.t('expenses_v2.list.filters.pending'),
              value: 'pending',
            },
            {
              label: i18n.t('expenses_v2.list.filters.changes_requested'),
              value: 'changes_requested',
            },
            {
              label: i18n.t('expenses_v2.list.filters.approved'),
              value: 'approved',
            },
            {
              label: i18n.t('expenses_v2.list.filters.rejected'),
              value: 'rejected',
            },
            {
              label: i18n.t('expenses_v2.list.filters.in_payroll'),
              value: 'in_payroll',
            },
            {
              label: i18n.t('expenses_v2.list.filters.sent_to_pay'),
              value: 'sent_to_pay',
            },
            {
              label: i18n.t('expenses_v2.expense.status.paid'),
              value: 'paid',
            },
          ],
        },
      ],
    },
    {
      identifier: 'accounting_date',
      title: i18n.t('expenses.settings.signed_invoices.list.table.attributes.accounting_date'),
      path: 'accountingDate',
      type: FilterType.Date,
      categories: [
        {
          kind: 'DatePicker',
          operation: 'between',
        },
      ],
    },
    {
      identifier: 'emitter_name',
      type: FilterType.String,
      categories: [
        {
          kind: 'SearchFilter',
          operation: 'contains',
        },
      ],
      path: 'emitterName',
      title: i18n.t('expenses.settings.signed_invoices.list.table.attributes.vendor'),
    },
  ]

  return (
    <DataTable
      source={source}
      filters={filters}
      persistFilters
      enableClearFilters
      disableExport
      pagination={{
        kind: 'offset',
        itemsPerPage: 15,
      }}
      sort={{ path: 'accountingDate', direction: 'desc' }}
      emptyState={<EmptyBlock />}
    />
  )
}

export default SignedInvoicesDatatable
